import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	type Injector,
	input,
	output,
	type Signal,
	type Type,
	ViewEncapsulation,
} from '@angular/core';
import { MatDivider } from '@angular/material/divider';

import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import { EffFocusTrapDirective } from '@efficiently/client-ui-interaction';
import { EffBadgeComponent, EffIconButtonComponent } from '@efficiently/client-ui-kit';

import { CLARITY_FLY_IN_RESIZE } from '../constants/clarity-fly-in-resize.constant';
import { ClarityFlyInResizeHandleComponent } from '../fly-in-resize-handle/clarity-fly-in-resize-handle.component';
import type { ClarityFlyInFocus } from '../models/clarity-fly-in-focus.model';
import type { ClarityFlyInTitleWeight } from '../models/clarity-fly-in-title-weight.model';
import { ClarityFlyInRef } from './clarity-fly-in-ref';

/**
 * The name of the fly-in's close control, which has no visible text, when the caller states none.
 *
 * One default rather than a name per screen, because a control named differently per screen is exactly
 * the drift a shared primitive exists to prevent. `ClarityFlyInConfig.closeLabel` is the one exception:
 * a ported brain panel passes the name brain gives its own control (`ApprovalDrawer`'s `Close dialog`).
 */
const CLOSE_LABEL = 'Close';

/**
 * The stem of the id `aria-labelledby` points at when the panel has a visible heading.
 *
 * Numbered per container rather than one constant, because stacked panels share the document: two
 * open panels with one heading id would each name themselves by whichever heading the browser finds
 * first. The component is `ViewEncapsulation.None` and internal to the library — it is never placed
 * in a template — so the counter below is the only thing that stamps these ids.
 */
const TITLE_ELEMENT_ID_PREFIX = 'eff-clarity-fly-in-panel-title';

let nextTitleElementId = 0;

/** Ported from Clarity `apps/web/src/shared/ui/SidePanel.tsx` (commit `f9217bb5a`). */
/**
 * The element inside a Clarity fly-in's CDK pane — everything a CDK overlay does *not* give you.
 *
 * A `@angular/cdk/overlay` overlay is a pane, a backdrop and a scroll strategy; the dialog role, the
 * accessible name, the focus trap and the close affordance are all this component's. They are on the
 * **same** `<section>` deliberately: an `aria-modal` or an accessible name bound to a wrapper that
 * carries no role is read by nothing, and a test resolving that wrapper certifies an attribute that
 * does not exist.
 *
 * **The panel owns its chrome, and owns it once.** The heading, the single close control and the
 * pinned footer region are all drawn here, so a body rendered inside a fly-in draws no header of its
 * own. That is what retired the two-close-controls state this primitive shipped with: a fly-in body
 * used to be a `<eff-clarity-dialog-frame>`, which brings its own header and its own close control,
 * and the reader saw both. The frame stays the dialog's; a fly-in body is content and nothing else.
 *
 * Internal by contract — created by `ClarityFlyIn`, never exported from the barrel and never placed
 * in a template. It is the library's one `ViewEncapsulation.None` component, because the CDK pane it
 * is attached into sits **outside** its own view and no encapsulated selector can reach it; the
 * sanctioned alternative to `::ng-deep` is `ViewEncapsulation.None` plus a `panelClass`, and every
 * rule in the stylesheet is scoped under `CLARITY_FLY_IN_PANEL_CLASS` or this element's own tag.
 */
@Component({
	selector: 'eff-clarity-fly-in-container',
	imports: [
		ClarityFlyInResizeHandleComponent,
		EffBadgeComponent,
		EffFocusTrapDirective,
		EffIconButtonComponent,
		MatDivider,
		NgComponentOutlet,
		NgTemplateOutlet,
		TestIdDirective,
	],
	templateUrl: './clarity-fly-in-container.component.html',
	styleUrl: './clarity-fly-in-container.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class ClarityFlyInContainerComponent {
	/** The caller's body component, created with {@link contentInjector}. */
	public readonly content = input.required<Type<unknown>>();
	/** Carries `ClarityFlyInRef` and `CLARITY_OVERLAY_DATA_TOKEN` down to the body. */
	public readonly contentInjector = input.required<Injector>();
	/** Drives `aria-modal` only — the backdrop and the scroll strategy are the overlay's own config. */
	public readonly modal = input<boolean>(true);
	/** Only `trap` confines Tab; a ported side panel moves focus in without one (`ClarityFlyInFocus`). */
	public readonly focus = input<ClarityFlyInFocus>('trap');
	/** The heading in the header row, and the panel's accessible name when it is set. */
	public readonly title = input<string>();
	/** The weight {@link title} is drawn at — `ClarityFlyInConfig.titleWeight`. */
	public readonly titleWeight = input<ClarityFlyInTitleWeight>('default');
	/** The line under the heading — `ClarityFlyInConfig.subtitle`. */
	public readonly subtitle = input<Signal<string>>();
	/** The caller's footer component, if any, created with the same {@link contentInjector}. */
	public readonly footer = input<Type<unknown>>();
	/** The panel's accessible name when it has no {@link title}. Absent means the panel has none. */
	public readonly ariaLabel = input<string>();
	/** The count beside the heading, shown while it is above zero — `ClarityFlyInConfig.countBadge`. */
	public readonly countBadge = input<Signal<number>>();
	/** The close control's name — `ClarityFlyInConfig.closeLabel`, else {@link CLOSE_LABEL}. */
	public readonly closeLabel = input<string>();
	/** Whether the body region draws its own inset and fill — `ClarityFlyInConfig.bodyInset`. */
	public readonly bodyInset = input<boolean>(true);
	/** The narrowest width, in px, the reader may resize the panel to. */
	public readonly minResizeWidth = input<number>(CLARITY_FLY_IN_RESIZE.MIN_WIDTH);

	/** The close control was pressed. The service turns this into `ClarityFlyInRef.close()`. */
	public readonly closeRequested = output<void>();

	private readonly flyInRef = inject(ClarityFlyInRef);

	protected readonly resolvedCloseLabel = computed<string>(() => this.closeLabel() ?? CLOSE_LABEL);
	/** The badge's count; `0` without a badge, which renders nothing, as brain's `countBadge > 0` check. */
	protected readonly badgeCount = computed<number>(() => this.countBadge()?.() ?? 0);
	/** The subtitle's text — the body's override over the caller's — trimmed; `''` without one, which renders nothing. */
	protected readonly resolvedSubtitle = computed<string>(
		() => (this.flyInRef.subtitleOverride() ?? this.subtitle()?.())?.trim() ?? '',
	);
	protected readonly titleElementId = `${TITLE_ELEMENT_ID_PREFIX}-${nextTitleElementId++}`;
	/** The ref's switch for the header close — the reveal-once state turns it off (`setHeaderCloseDisabled`). */
	protected readonly closeDisabled = this.flyInRef.headerCloseDisabled;
	/** The body's control ahead of the heading — `ClarityFlyInRef.setHeaderLeading`. */
	protected readonly headerLeading = this.flyInRef.headerLeading;
	/** The body's header controls beside the close — `ClarityFlyInRef.setHeaderActions`. */
	protected readonly headerActions = this.flyInRef.headerActions;
	/** The body's footer controls below the scroll — `ClarityFlyInRef.setFooterActions`. */
	protected readonly footerActions = this.flyInRef.footerActions;

	/**
	 * `aria-modal` is present only on a modal panel. A non-modal fly-in leaves the page behind it
	 * operable, so claiming modality would tell assistive technology the opposite of the truth.
	 */
	protected readonly ariaModal = computed<string | null>(() => (this.modal() ? 'true' : null));

	/**
	 * The trap is on only for a modal panel with the `trap` focus policy. A non-modal panel disclaims
	 * modality, and brain's side panels never confine Tab (brain defect #37) — they move focus in
	 * themselves through the ref instead.
	 */
	protected readonly trapsFocus = computed<boolean>(() => this.modal() && this.focus() === 'trap');

	/**
	 * The heading to render, or `undefined` when there is none to render.
	 *
	 * **One answer to "is there a heading?", read by all three consumers** — the template, the
	 * `aria-labelledby` below and the `aria-label` below that. A blank or whitespace-only `title` is a
	 * value a caller reaches by accident rather than on purpose (a heading derived from data that came
	 * back empty), and it is normalised here so the three cannot disagree about it. They did once: the
	 * template's `@if` treats `''` as no heading while `'' !== undefined` is `true`, so a blank title
	 * left `aria-labelledby` pointing at an element that was never rendered **and** suppressed the
	 * `ariaLabel` that would otherwise have named the panel — a dialog with no accessible name at all
	 * (WCAG 4.1.2), which is strictly worse than passing no title.
	 */
	protected readonly resolvedTitle = computed<string | undefined>(() => {
		const title = (this.flyInRef.titleOverride() ?? this.title())?.trim();

		return title === undefined || title.length === 0 ? undefined : title;
	});

	/**
	 * The heading's id when there is a heading, so the panel is named by text the reader can see.
	 *
	 * This is what closes the design doc's "unnamed panel" question for every caller that states a
	 * `title`: the name is the rendered heading itself, not a string duplicated beside it, so the two
	 * cannot drift apart and Label-in-Name (WCAG 2.5.3) holds by construction.
	 */
	protected readonly ariaLabelledBy = computed<string | null>(() =>
		this.resolvedTitle() === undefined ? null : this.titleElementId,
	);

	/**
	 * `aria-label` only when nothing visible names the panel.
	 *
	 * Both names at once is not "more accessible": a screen reader would announce the `aria-label`
	 * and the sighted reader would read the heading, and a control whose spoken name does not contain
	 * its visible one is the Label-in-Name failure. The heading wins because it is the one both can
	 * perceive.
	 */
	protected readonly resolvedAriaLabel = computed<string | null>(() =>
		this.resolvedTitle() === undefined ? (this.ariaLabel() ?? null) : null,
	);

	protected onClose(): void {
		this.closeRequested.emit();
	}

	protected onResized(width: number): void {
		this.flyInRef.setWidth(width);
	}
}
